/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {get} from '@ion/core/src/environment/environment';
import ApplicationFeatures, {GetPermittedRoutesParams} from '@ion/core/src/security/ApplicationFeatures';
import {hasRole} from '@ion/core/src/security/helpers';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Entity,
  GenericObject,
  Link,
  Role,
  User
} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {Route} from '@/routes/RouteDefinitions';
import {generateAllModuleActions} from '@/security/FeatureModuleActions';
import {hasInternalVehicleEngineeringRole} from '@/security/ModuleAccessControl';

/**
 * Provides a reference to all pre-defined CTC Admin Application Features.
 * Each Feature is issued a unique Id which is used across all environments.
 *
 * The name of each feature is arbitrary and used for natural reference,
 * whereas the value of each feature represents the absolute Id of the
 * Feature.
 *
 * Each Feature id is defined, and assigned to a Feature within the CTC Admin
 * (CTC Admin) Application Entity's extendedAttributes value in each
 * environment.
 */
export const DASHBOARD_PAGE                       = 'd45d4cd5-6de4-428b-a946-477424d6f585';
export const DEVICES                              = '57f510b3-4b9c-4a63-bf26-337fd6bf3c58';
export const PROVISIONING                         = 'a371f9cb-1fc3-440c-b324-140431b62d11';
export const PROVISIONING_DOWNLOAD_JOBS           = '6916303b-9020-4b68-830f-160ce4d011eb';
export const PROVISIONING_JOBS_SEARCH_ENGINE      = '26308ef3-32b8-4f85-ba4b-6f056c4a8168';
export const PROVISIONING_DEVICE_FILES            = '6f19aadf-40a7-426e-89eb-e7edbf598c0f';
export const PROVISIONING_DEVICE_TEMPLATES        = '41f64f97-c0d9-411c-8e2a-bb5072c57418';
export const PROVISIONING_SCHEDULE_DOWNLOADS      = 'f0051206-de14-4ca8-ba13-ea014d80f87b';
export const ALERTS                               = '3ef8804f-629e-4698-a3e3-afee131fba69';
export const ALERTS_SETUP                         = 'c1bc107c-4023-4c41-95c0-2509c0b3575f';
export const ALERTS_NOTIFICATIONS                 = 'dfda6bc3-80b0-4a44-8b81-d0fb0e930e7f';
export const REPORTS                              = 'fc269af8-72c0-4b57-8c3c-028ed0b5ec08';
export const REPORTS_AVAILABLE_REPORTS            = '49a543f7-6e64-48ff-98aa-8d1c5fa70e9c';
export const REPORTS_SCHEDULED_REPORTS            = '26e49e3f-e2d1-48ed-9edf-e4584f770c7f';
export const ANALYTICS_PAGE                       = '1e35a639-1f38-073f-42af-80eaaab4ea3b';
export const ADMIN                                = '6ba276d4-3f37-4890-a57d-b3044c286c60';
export const ADMIN_ACCOUNTS                       = 'cbdf8164-d7bf-4032-a2e9-a99a10e006f8';
export const ADMIN_APPLICATIONS                   = '1972c656-b472-49e1-9ab0-ab0c69640455';
export const ADMIN_ASSETS                         = '56d5bc66-0782-4d59-8f88-0e6c683535f9';
export const ADMIN_PEG_LABELS                     = 'dc20f01d-446a-4295-8e54-9b56fb992a1a';
export const ADMIN_PEG_BEHAVIORS                  = '16099af0-d13d-4a4a-9dd7-39734a0aaede';
export const ADMIN_AEMP_CONFIG                    = 'fcefbe1d-40b3-463f-85a3-239aa5d3188e';
export const ADMIN_USERS                          = '926fd6ef-161e-43f5-8491-c2ea3861ece2';
export const ADMIN_ROLES                          = 'f24d4d45-b0bf-4d8f-a8de-212dfcf5c685';
export const ADMIN_GROUPS                         = 'ffee852d-6627-4002-a67e-6ce27171b5b9';
export const ADMIN_UTIL                           = 'e328182c-5f2d-4a17-bbdb-9d588ac70085';
export const ADMIN_DEVICE_CONFIGS                 = '45d19b6d-9356-4578-8f50-44a92582e2bb';
export const SYSTEM_CONFIG                        = '4fa17eee-9118-4ecd-b81e-aa236fd197ba';
export const SYSTEM_CONFIG_ALERT_TYPES            = '320ceaff-851c-41ac-b5d3-60cd768228bb';
export const SYSTEM_CONFIG_APPLICATIONS           = 'a9e8d8d2-ca04-4f28-89a7-cca03ea868b8';
export const SYSTEM_CONFIG_CARRIERS               = '81753b26-00a6-44af-bb34-ac0213c1ec7c';
export const SYSTEM_CONFIG_DEVICE_TYPES           = '697cb690-4834-4008-ba6e-3ffa18c26075';
export const SYSTEM_CONFIG_AIR_ID_CREATION        = '689c2fe0-12da-4f7e-b79d-ffeef0c60db1';
export const SYSTEM_CONFIG_BULK_ACTIVATION        = 'af15036a-5511-428a-8d6c-b7ed58e330e6';
export const SYSTEM_CONFIG_DEVICE_CARRIER         = 'df2a2cb9-3a29-4513-b409-082e4f5295cf';
export const AUDIT_lOGS                           = '5a8ec110-9613-4f38-9efc-0d7e786d442d';
export const TOOLBOX                              = '27133f61-ddcb-46d9-92fb-c4a952670bdf';
export const TOOLBOX_KEYFILE_BUILDER              = '6d94c792-d32c-43b6-ba4f-2404c5e4e157';
// TODO: UPDATE TOOLBOX MODBUS EDITOR FEATURE ID WHEN IMPLEMENTING FEATURE ...
export const TOOLBOX_MODBUS_EDITOR                = '11111111-1111-1111-1111-111111111111';
export const TOOLBOX_MODBUS                       = '6158d42d-4c26-4acc-a406-11dca2976a38';
export const MANUFACTURING                        = 'f73e8fd3-2b19-7c6b-a958-963d1b2c4f46';
export const MANUFACTURED_DEVICES                 = 'a83c7fd1-9e26-4b7d-b539-283d6b4f9c15';
export const MANUFACTURING_INVENTORY              = 'e92b6da4-7f36-2c1b-a849-793d6b5f2c46';
export const MANUFACTURING_APPLICATION_IDS        = 'b73d6fa2-1e96-4c7b-a539-283d6b4f9c15';
export const MANUFACTURING_PART_NUMBERS           = 'f93e8bd3-2b19-7c6b-a958-963d1b2c4f46';
export const MANUFACTURING_SHIPMENTS              = 'ae052d7d-a242-f5ce-2bb6-2bef59f4a1b1';
// TODO: UPDATE VEHICLE FEATURE ID WHEN IMPLEMENTING FEATURE ...
export const VEHICLE                              = 'ea028a6c-64c0-427d-80af-c9b675222a7e';
export const VEHICLE_MANAGER                      = '1bf27ac7-f091-4abb-99b8-15c9467d0fde';
export const VEHICLE_PROFILE                      = '3c2939ce-caee-4fa8-97e4-c66b61dc8a34';
export const OEM_VALIDATION_RULES                 = '1ddc6d62-62a9-496a-9de7-75f78320ccc5';
export const FILE_PERMISSIONS                     = '9a97f161-7a3f-46ab-8c04-4b79c29dff8d';
export const FILE_PERMISSIONS_SCRIPT              = 'd5972faa-834c-4c1e-a18d-a42cf3e3cb80';
export const FILE_PERMISSIONS_LMU_FW_CO_PRO       = '20e4f2b8-966c-4ac3-ba67-d2ea9c62d7da';
export const FILE_PERMISSIONS_VBUS                = '227eb22e-79e0-4072-917b-75e02526d9d7';
export const FILE_PERMISSIONS_BLUE_TOOTH          = '78bd342a-3af4-477d-8f45-a429353fbbda';
export const FILE_PERMISSIONS_RADIO_FW            = '97f5dd2a-7112-4924-8311-1dab797cf5ef';
export const FILE_PERMISSIONS_HOSTED_EDGE_OPKGS   = '4ad1dd42-4046-431a-8115-260ff597216d';
export const FILE_PERMISSIONS_CUSTOMER_DB_FILES   = '71bb275e-c8db-44ec-8eec-3c10b4a12a24';
export const FILE_PERMISSIONS_MDT                 = 'c87350dc-851e-4bad-a60d-ce9dc1b62ad8';
export const FILE_PERMISSIONS_KEY_FILES           = '4f52d226-70f0-40e3-94b4-9b8549c4f829';
export const SENSORS                              = 'ca81ba1a-5985-46f4-9239-ba87e3f3b042';
export const SENSOR_INSTALLER                     = 'fb2419cb-ef00-434d-8aa3-a46090bf15ff';

export const CALAMP_FILE_CREATE_PERMISSION        = 'calampselectfile-create';
export const CUSTOMER_FILE_CREATE_PERMISSION      = 'customerfile-create';

const parentFeatures = {
  [ADMIN] : {
    ADMIN_AEMP_CONFIG,
    ADMIN_APPLICATIONS,
    ADMIN_ASSETS,
    ADMIN_USERS,
    ADMIN_ACCOUNTS,
    ADMIN_ROLES,
    ADMIN_GROUPS,
    ADMIN_PEG_BEHAVIORS,
    ADMIN_PEG_LABELS,
    ADMIN_UTIL,
    ADMIN_DEVICE_CONFIGS
  },
  [PROVISIONING] : {
    PROVISIONING_DOWNLOAD_JOBS,
    PROVISIONING_JOBS_SEARCH_ENGINE,
    PROVISIONING_DEVICE_FILES,
    PROVISIONING_DEVICE_TEMPLATES,
    PROVISIONING_SCHEDULE_DOWNLOADS
  },
  [REPORTS] : {
    REPORTS_AVAILABLE_REPORTS,
    REPORTS_SCHEDULED_REPORTS
  },
  [ALERTS] : {
    ALERTS_SETUP,
    ALERTS_NOTIFICATIONS
  },
  [SYSTEM_CONFIG] : {
    SYSTEM_CONFIG_ALERT_TYPES,
    SYSTEM_CONFIG_APPLICATIONS,
    SYSTEM_CONFIG_BULK_ACTIVATION,
    SYSTEM_CONFIG_CARRIERS,
    SYSTEM_CONFIG_DEVICE_CARRIER,
    SYSTEM_CONFIG_DEVICE_TYPES,
    SYSTEM_CONFIG_AIR_ID_CREATION
  },
  [TOOLBOX] : {
    TOOLBOX_KEYFILE_BUILDER,
    TOOLBOX_MODBUS
  },
  [MANUFACTURING] : {
    MANUFACTURING_INVENTORY,
    MANUFACTURED_DEVICES,
    MANUFACTURING_APPLICATION_IDS,
    MANUFACTURING_PART_NUMBERS,
    MANUFACTURING_SHIPMENTS
  },
  [VEHICLE] : {
    VEHICLE_MANAGER,
    VEHICLE_PROFILE,
    OEM_VALIDATION_RULES
  },
  [SENSORS] : {
    SENSOR_INSTALLER
  }
};

// defines the features which restricted accounts have permission to access;
// if a feature is not present, a restricted account does NOT have permission
// to access it ...
const _restrictedAccountAllowedFeatures = [];

// defines the feature ids which is restricted temporarily. It will not be
// showed while create roles.
const _restrictedFeatures = {
  [PROVISIONING_DEVICE_TEMPLATES]   : true,
  [PROVISIONING_SCHEDULE_DOWNLOADS] : true,
  [SYSTEM_CONFIG_AIR_ID_CREATION]   : true,
  [SYSTEM_CONFIG_BULK_ACTIVATION]   : true,
  [SYSTEM_CONFIG_DEVICE_CARRIER]    : true,
  [SYSTEM_CONFIG]                   : true,
  [SYSTEM_CONFIG_ALERT_TYPES]       : true,
  [SYSTEM_CONFIG_APPLICATIONS]      : true,
  [SYSTEM_CONFIG_CARRIERS]          : true,
  [ADMIN_UTIL]                      : true,
  [SYSTEM_CONFIG_DEVICE_TYPES]      : true,
  [REPORTS_SCHEDULED_REPORTS]       : true
};

// Define the list of featues for mapping the modules with application keys.
const _featureModules = {
  ADMIN_ACCOUNTS,
  ADMIN_AEMP_CONFIG,
  ADMIN_APPLICATIONS,
  ADMIN_ASSETS,
  ADMIN_GROUPS,
  ADMIN_PEG_BEHAVIORS,
  ADMIN_PEG_LABELS,
  ADMIN_ROLES,
  ADMIN_USERS,
  ADMIN_DEVICE_CONFIGS,
  ALERTS_NOTIFICATIONS,
  ALERTS_SETUP,
  DEVICES,
  DASHBOARD_PAGE,
  PROVISIONING_DEVICE_FILES,
  PROVISIONING_DOWNLOAD_JOBS,
  PROVISIONING_JOBS_SEARCH_ENGINE,
  REPORTS_AVAILABLE_REPORTS,
  REPORTS_SCHEDULED_REPORTS,
  SYSTEM_CONFIG_ALERT_TYPES,
  SYSTEM_CONFIG_APPLICATIONS,
  SYSTEM_CONFIG_CARRIERS,
  SYSTEM_CONFIG_DEVICE_TYPES,
  TOOLBOX_KEYFILE_BUILDER,
  TOOLBOX_MODBUS,
  MANUFACTURED_DEVICES,
  MANUFACTURING_INVENTORY,
  MANUFACTURING_APPLICATION_IDS,
  MANUFACTURING_PART_NUMBERS,
  MANUFACTURING_SHIPMENTS,
  VEHICLE_MANAGER,
  VEHICLE_PROFILE,
  OEM_VALIDATION_RULES,
  FILE_PERMISSIONS_SCRIPT,
  FILE_PERMISSIONS_LMU_FW_CO_PRO,
  FILE_PERMISSIONS_VBUS,
  FILE_PERMISSIONS_BLUE_TOOTH,
  FILE_PERMISSIONS_RADIO_FW,
  FILE_PERMISSIONS_HOSTED_EDGE_OPKGS,
  FILE_PERMISSIONS_CUSTOMER_DB_FILES,
  FILE_PERMISSIONS_MDT,
  FILE_PERMISSIONS_KEY_FILES,
  SENSOR_INSTALLER
};

// Define the list of file permission for mapping the modules with application key.
const _filePermissionModules = {
  FILE_PERMISSIONS
};

// Define the list of device file types platform ids.
const mapSubTypeCategory = {
  [Entity.lmuscript]                : ['366', '373'],
  [Entity.lmufirmware]              : ['365', '781'],
  [Entity.vbusconfig]               : ['761', '375'],
  [Entity.vbusfirmware]             : ['374', '393', '394', '399', '419'],
  [Entity.vbushostedapp]            : ['376'],
  [Entity.bluetoothfirmware]        : ['398', '400', '404', '406', '407', '410', '415', '416', '417', '418', '420', '421'],
  [Entity.radiofirmware]            : ['405', '767'],
  [Entity.custompackagesedgeapp]    : ['825'],
  [Entity.custompackageshostedapp]  : ['381', '383', '384', '385', '386', '387', '389', '390', '391', '392', '864'],
  [Entity.custompackagesopkg]       : ['402', '403'],
  [Entity.customerdatabasedriverid] : ['367'],
  [Entity.customerdatabasegeozone]  : ['368'],
  [Entity.customerdatabaseapn]      : ['369'],
  [Entity.mdtfirmware]              : ['396'],
  [Entity.mdtconfig]                : ['397'],
  [Entity.lmudigitalsignature]      : ['370']
};

/**
   * Map the Features and Permissions for CTC Admin Role creation.
   * When Feature is getting selected the same time permissions also
   * auto selected based on the feature id matching with permission
   * resource name.
   */
const mapFeaturePermissions = {
  [ADMIN_APPLICATIONS]                 : [Entity.application],
  [ADMIN_ASSETS]                       : [Entity.asset],
  [ADMIN_AEMP_CONFIG]                  : [Entity.aemppollerconfigurator],
  [PROVISIONING_DEVICE_FILES]          : [Entity.filemeta, Entity.customerfile],
  [ADMIN_DEVICE_CONFIGS]               : [Entity.deviceconfiguration],
  [DEVICES]                            : [Entity.device],
  [ADMIN_ACCOUNTS]                     : [Entity.account],
  [PROVISIONING_DOWNLOAD_JOBS]         : [Entity.jobs, Entity.job, Entity.task],
  [PROVISIONING_JOBS_SEARCH_ENGINE]    : [Entity.jobs, Entity.job, Entity.task],
  [REPORTS_AVAILABLE_REPORTS]          : [Entity.reports],
  [ADMIN_PEG_LABELS]                   : [Entity.peglabel],
  [ADMIN_PEG_BEHAVIORS]                : [Entity.pegbehavior],
  [ADMIN_ROLES]                        : [Entity.role],
  [ADMIN_USERS]                        : [Entity.user],
  [ADMIN_GROUPS]                       : [Entity.group],
  [TOOLBOX_KEYFILE_BUILDER]            : [Entity.keyfilegenerator],
  [TOOLBOX_MODBUS]                     : [Entity.filemeta, Entity.customerfile],
  [MANUFACTURING_INVENTORY]            : [Entity.manufacturing],
  [MANUFACTURED_DEVICES]               : [Entity.manufacturing],
  [MANUFACTURING_APPLICATION_IDS]      : [Entity.appid, Entity.manufacturing],
  [MANUFACTURING_PART_NUMBERS]         : [Entity.partnumber, Entity.manufacturing],
  [MANUFACTURING_SHIPMENTS]            : [Entity.shipment],
  [VEHICLE_MANAGER]                    : [Entity.vin, Entity.vehicleidreport],
  [VEHICLE_PROFILE]                    : [Entity.vin],
  [OEM_VALIDATION_RULES]               : [Entity.vin],
  [SENSOR_INSTALLER]                   : [Entity.asset, Entity.device, Entity.tpms],
  [FILE_PERMISSIONS_SCRIPT]            : [Entity.lmuscript],
  [FILE_PERMISSIONS_LMU_FW_CO_PRO]     : [Entity.lmufirmware],
  [FILE_PERMISSIONS_VBUS]              : [Entity.vbusconfig, Entity.vbusfirmware, Entity.vbushostedapp],
  [FILE_PERMISSIONS_BLUE_TOOTH]        : [Entity.bluetoothfirmware],
  [FILE_PERMISSIONS_RADIO_FW]          : [Entity.radiofirmware],
  [FILE_PERMISSIONS_HOSTED_EDGE_OPKGS] : [
    Entity.custompackagesedgeapp,
    Entity.custompackageshostedapp,
    Entity.custompackagesopkg
  ],
  [FILE_PERMISSIONS_CUSTOMER_DB_FILES] : [
    Entity.customerdatabasedriverid,
    Entity.customerdatabasegeozone,
    Entity.customerdatabaseapn
  ],
  [FILE_PERMISSIONS_MDT]       : [Entity.mdtconfig, Entity.mdtfirmware],
  [FILE_PERMISSIONS_KEY_FILES] : [Entity.lmudigitalsignature]
};

/**
 * The Features API provides the single, authoritative point of entry for initializing,
 * validating, and determining CTC Admin Feature  availability for authenticated Users
 * based on the Features available to the User's CTC Admin Roles.
 *
 * CTC Admin Features are defined within the features.json document of the doctc-ui-seed
 * project located at:
 *
 * @see http://bitbucket.pd.calampcorp.com:7990/projects/CTC Admin/repos/doctc-ui-seed/browse
 * @see http://bitbucket.pd.calampcorp.com:7990/repos/doctc-ui-seed/browse/src/json/features.json
 *
 *
 * ```typescript
 *
 *   import Features from '@/security';
 *
 *   [LOAD_ROLES_RESPONSE]: (draft, {roles: Role[]}) => {
 *     Features.initialize({
 *       user        : draft.user,
 *       allFeatures : draft.features,
 *       appKey      : get('appKey'),
 *       roles
 *     });
 *     ...
 *   }
 *
 * ```
 *
 * @class Features
 */
class Features extends ApplicationFeatures {
  /**
   * @private
   */
  _applications = {
    ica : 'bd58ef15-9936-4fca-a52a-be78d73c6e96',
    fnl : '9fed2e7a-55bf-49e1-b316-626dc915507e'
  }

  /**
   * Indicates whether the current account is an iOn Suite restricted account.
   * (Initialized to null so that the initial setCurrentAccount() always returns
   * restrictedChanged = true)
   */
  _isCurrentAccountRestricted = null;

  /**
   * Indicates whether the user has access permission to add or edit CalAmp (Device) Files
   */
  _calampFileAllowed = null;

  /**
   * Indicates whether the user has access permission to add or edit Customer (Device) Files
   */
  _customerFileAllowed = null;

  /**
    * Indicates whether the specified feature (ID) is restricted for the current
    * account.
    */
  _isFeatureRestricted = (id: string): Boolean => {
    return _restrictedAccountAllowedFeatures.indexOf(id) < 0;
  }

  /**
   * Determines if the given feature (via id) is temporarily restricted, which means
   * it should never be shown to (all) users.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isRestrictedFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isRestrictedFeature(UTILS);                   // true
   *   const showFeature = Features.isRestrictedFeature(ADMIN_ACCOUNTS);          // false
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isRestrictedFeature = (id: string): boolean => {
    return !!_restrictedFeatures[id];
  }

  /**
   * Get the feature id based on the requested
   * feature to the _featreModules object.
   */
  checkFeatureId = (feature: string): Boolean => {
    return _featureModules[feature];
  }

  /**
   * Get the feature id based on the requested
   * feature to the _filePermissionsModules object.
   */
  checkFilePermissionId = (feature: string): Boolean => {
    return _filePermissionModules[feature];
  }

  /**
   * Get the file permission id based on the requested
   * feature id to the mapSubTypeCategory object.
   */
  getFilePermissionsPlatformsIds = (permissionsKey: string) => {
    return mapSubTypeCategory[permissionsKey];
  }

  /**
   * Retrieves the statically mapped permissions for the given feature (via id).
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const inventoryFeaturePermissions = Features.getMappedPermissions(MANUFACTURING_INVENTORY);
   *
   *   inventoryFeaturePermissions; // [Entity.manufacturing]
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  getMappedPermissions = (featureId: string): GenericObject => {
    return mapFeaturePermissions[featureId];
  }

  /**
   * Determines if the given feature (via id) is a device feature.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isDeviceFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isDeviceFeature(DEVICES);                 // true
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isDeviceFeature = (featureId: string): boolean => {
    return _featureModules[featureId] === DEVICES;
  }

  /**
    * Indicates whether the specified account (ID) is a restricted account.
    *
    * ```typescript
    *
    * import Features from '@/security/Features';
    *
    * const isRestricted = Features.isAccountRestricted(user.account.id);
    *
    * ```
    *
    */
  isAccountRestricted(id: number): boolean {
    const restrictedAccountIds = get('restrictedAccountIds');
    return Boolean(restrictedAccountIds?.indexOf(id) >= 0);
  }

  /**
    * Indicates whether the current account is a restricted account.
    *
    * ```typescript
    *
    * import Features from '@/security/Features';
    *
    * if (Features.isCurrentAccountRestricted()) {
    *   // hide a given component
    * }
    *
    * ```
    *
    */
  isCurrentAccountRestricted = (): boolean => {
    return Boolean(this._isCurrentAccountRestricted);
  }

  /**
    * Sets whether the current account is restricted. Intended to be called at
    * initialization/login and whenever else the global group/account is set.
    *
    * ```typescript
    *
    * import Features from '@/security/Features';
    *
    * const {isRestricted, restrictedChanged} = Features.setCurrentAccount(user.account);
    * if (restrictedChanged) {
    *   if (isRestricted) {
    *     // halt notifications
    *   } else {
    *     // start notifications
    *   }
    * }
    *
    * ```
    *
    */
  setCurrentAccount(account: Link): { isRestricted: boolean, restrictedChanged: boolean } {
    const wasRestricted = this._isCurrentAccountRestricted;
    this._isCurrentAccountRestricted = this.isAccountRestricted(entityId(account));
    const isRestricted = this._isCurrentAccountRestricted;
    const restrictedChanged = wasRestricted !== isRestricted;
    return {isRestricted, restrictedChanged};
  }

  /**
   * Determines if the authenticated user has permissions for CTC Admin Application.
   *
   * @method  hasCTCAdmin
   * @returns {boolean} True if user has the iOn Application assigned (iOn) assigned,
   *          otherwise, false.
   */
  hasCTCAdmin() {
    return hasCTCAdmin(this._user);
  };

  /**
   * Returns the default route for restricted accounts if the current account
   * is restricted, otherwise, returns the specified route.
   *
   * ```typescript
   *
   * import Features from '@/security/Features';
   *
   * const route = Features.getDefaultRoute(Route.TRACKING);
   *
   * ```
   *
   */
  getDefaultRoute(defaultRoute?: Route): Route {
    return defaultRoute || Route.DASHBOARD;
  }

  /**
   * Determines if the current user can access all specified features,
   * taking into account roles whether the user's roles has permissions.
   * See ApplicationFeatures.hasFeature() for more information.
   */
  hasFeature(...ids): boolean {
    let permitted = super.hasFeature(...ids);
    if (!permitted) {
      ids.forEach(id => {
        const subFeatures = parentFeatures[id];
        if (subFeatures && super.hasAny(...Object.values(subFeatures))) {
          permitted = true;
        }
      });
    }

    return permitted;
  }

  /**
   * Returns the permitted pages for the current user, taking into account
   * whether the user's roles has permissions.
   * See ApplicationFeatures.getPermittedPages() for more information.
  */
  getPermittedPages({
    pages,
    permitted = [],
    isChild = false,
    parent = null
  }: GetPermittedRoutesParams) {
    let permittedPages = super.getPermittedPages({pages, permitted, isChild, parent});
    permittedPages = permittedPages.filter((feature) => feature.pages ? feature.pages.length > 0 : true);
    return permittedPages;
  }

  /**
   * Returns whether the current user can access the specified role, taking
   * into account whether the user's current account is restricted.
   * See ApplicationFeatures.canAccessRole() for more information.
   */
  canAccessRole(role: Role, verifyAssigned = false): boolean {
    return super.canAccessRole(role, verifyAssigned);
  }

  /**
   * Sets the additional permissions for specific features during ion-ctc-admin initialization.
   */
  setAdditionalPermissions(permissions: Link[]): void {
    this._calampFileAllowed = permissions.find(({title}) => title === CALAMP_FILE_CREATE_PERMISSION);
    this._customerFileAllowed = permissions.find(({title}) => title === CUSTOMER_FILE_CREATE_PERMISSION);
  };

  /**
   * Returns if the user has permission to add / edit CalAmp Device Files.
   */
  get calampFileAllowed(): boolean {
    return !!this._calampFileAllowed;
  };

  /**
   * Returns if the user has permission to add / edit Customer Device Files.
   */
  get customerFileAllowed(): boolean {
    return !!this._customerFileAllowed;
  };

  /**
   * Returns if the given feature id is for manufacturing.
   */
  isManufacturingFeature(featureId: string): boolean {
    return MFG_FEATURES.includes(featureId);
  }

  /**
   * @deprecated opt instead for isInternalRole
   *
   * Determines if the current user is considered an 'Internal Role', or 'Calamp Role'.
   *
   * Used to determine the visibility of components or allowance of extra data fetches.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const {roles} = useAuthenticatedUser();
   *   const showFeature = Features.isCalampRole(roles);
   *
   * ```
   *
   * @returns whether the currently logged in user is considered a calamp role.
   */
  isCalampRole(roles): boolean {
    return hasRole(roles, safeJSONParse(get('vbusRoles')));
  }

  /**
   * Determines if the current user is considered an 'Internal Role', or 'Calamp Role'.
   *
   * Used to determine the visibility of components or allowance of extra data fetches.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isInternalRole();
   *
   * ```
   *
   * @returns whether the currently logged in user is considered an internal role.
   */
  isInternalRole(): boolean {
    const internalRoles = safeJSONParse(get('vbusRoles'));
    const roles = this.getRoles(this._user);
    return !!roles.some(({id}: Role) => internalRoles.includes(id));
  }

  /**
   * Provides a convenience method which returns the ion-ctc-Admin Feature Modules.
   * generateAllModuleActions method will generate the modules with respective actions.
   * Mapping the modules and wrapped the modules into the application keys.
   */
  getFeatureModules = (api) => {
    const featureModules = [];
    const modules = generateAllModuleActions(api);
    modules?.map((item, index) => {
      const KEY = Object.keys(_featureModules)[index];
      const VALUE = Object.values(_featureModules)[index];
      featureModules[VALUE] = item[KEY];
    });
    return featureModules;
  };

  /**
   * Determines if the given feature (via id) is an internal feature, which means it should only be shown to
   * internal (Calamp) roles.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isInternalFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isInternalFeature(MANUFACTURED_DEVICES);    // true
   *   const showFeature = Features.isInternalFeature(ADMIN_ACCOUNTS);          // false
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isInternalFeature = (id: string): boolean => {
    return INTERNAL_FEATURES.includes(id);
  };

  /**
   * Determines if the given feature (via id) is a vehicle feature.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isVehicleFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isVehicleFeature(VEHICLE);                 // true
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isVehicleFeature = (featureId: string): boolean => {
    return featureId === VEHICLE;
  }

  /**
   * Determines if the given feature (via id) is a Vehicle Profile feature.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isVehicleProfileFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isVehicleProfileFeature(VEHICLE_PROFILE);         // true
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isVehicleProfileFeature = (featureId: string): boolean => {
    return featureId === VEHICLE_PROFILE;
  }

  /**
   * Determines if the given user has `Internal Vehicle Engineering` Role
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isUserHasInternalVESRole();
   *   ...
   *
   * ```
   *
   * @returns true if the user has `Internal Vehicle Engineering` Role.
   */
  isUserHasInternalVESRole = (): boolean => {
    return hasInternalVehicleEngineeringRole(this._user);
  }
}

// Currently restricted features protected by a feature flag.
const MFG_FEATURES = [
  MANUFACTURING,
  MANUFACTURED_DEVICES,
  MANUFACTURING_INVENTORY,
  MANUFACTURING_APPLICATION_IDS,
  MANUFACTURING_PART_NUMBERS,
  MANUFACTURING_SHIPMENTS
];

const INTERNAL_FEATURES = [
  MANUFACTURED_DEVICES,
  MANUFACTURING_APPLICATION_IDS,
  MANUFACTURING_PART_NUMBERS,
  MANUFACTURING_SHIPMENTS
];

/**
 * Determines if the authenticated user has application permissions
 * for CTC Admin.
 *
 * @method  hasCTCAdmin
 * @param   {object} user CTC User entity from which to determine if the
 *          user has the CTC Admin application assigned.
 * @returns {boolean} True if  user has the CTC Admin app, otherwise, false.
 */
export const hasCTCAdmin = (user: User, appKey?: string): boolean => {
  return Boolean(
    user?.applications.find(({href}) => href.indexOf(appKey || get('appKey')) > -1)
  );
};

export default new Features();
